import Layout from "@/components/layout/Layout";
import React, { useState } from "react";
import Sidepanel from "@/components/layout/Sidepanel";
import Content from "@/components/layout/Content";
import ContentOptions from "@/components/layout/ContentOptions";
import Heading from "@/components/elements/Heading";
import Question from "@/components/elements/Question";
import NavigationButtons from "@/components/elements/Buttons";
import Drawing from "@/components/drawing/drawing";
import { Inertia } from "@inertiajs/inertia";
import Label from "@/components/elements/Label";
import api from "@/services/api";

/**
 * Configurator Component
 *
 * Main product configuration interface that guides users through the step-by-step
 * process of configuring a custom product. Handles material selection, layer
 * configuration, validation, and real-time product visualization.
 *
 * @param {Object} props - Component props
 * @param {Object} props.session - Current session information
 * @param {string} props.shoppingurl - URL for shopping cart navigation
 * @param {string} props.step - Current configuration step identifier
 * @param {Array} props.steps - Available configuration steps
 * @param {Object} props.product - Initial product configuration
 * @param {Object} props.product.properties - Product properties
 * @param {string} props.heading - Page heading text
 * @param {string} props.subheading - Page subheading text
 * @param {Array} props.questions - Configuration questions for current step
 * @param {string} props.saveUrl - API endpoint for saving configuration
 * @param {string} props.prevLink - URL for previous step navigation
 * @param {string} props.nextLink - URL for next step navigation
 * @param {string} props.nextLinkSec - Alternative next step URL for special cases
 * @param {boolean} props.insideview - Whether to show inside view in drawing (default: false)
 * @param {Array} props.colors - Available color options for visualization
 *
 * @returns {JSX.Element} The rendered configuration page
 */
export default function Configurator({
    session,
    shoppingurl,
    step,
    steps,
    product,
    heading,
    subheading,
    questions,
    saveUrl,
    prevLink,
    nextLink,
    nextLinkSec,
    insideview = false,
    colors,
}) {
    // Initialize state with product data
    const [productprops, setProductprops] = useState(product);
    const [properties, setProperties] = useState(product.properties);
    const [errors, setErrors] = useState([]);
    const [quests, setQuests] = useState(questions);

    // Validation is required for outer and inner layer steps and kernplaat
	// TODO: what the hell
    const validateCheck =
        step === "opbouw.buitenlaag" || step === "opbouw.binnenlaag" || step === "opbouw.kernplaat" || step === "opbouw.isolatie" || step === "opbouw.isolatie1" || step === "opbouw.isolatie2";
    const [stepList, setStepList] = useState(steps);
    const [nextLinkObj, setNextLink] = useState(nextLink);

    /**
     * Update question properties when API calls return new options
     * Used to populate dropdowns with server-fetched data
     *
     * @param {Array} value - New options array from server
     * @param {string} property - Property name for which options are being updated
     */
    function updateQuestionProperties(value, property) {
        let quest = [...quests];
        let neededfield = quest[0].fields.find((x) => x.property === property);
        if (neededfield) {
            neededfield.options = value;
        }
        setQuests(quest);
    }

    /**
     * Update available navigation steps and handle special routing
     * Fetches updated step list from server and handles insulation-specific routing
     */
    function updateSteps() {
        api.get(
            `/api/steps/${product.session_id}/${product.product_id}/${step}`,
        ).then((response) => {
            setStepList(response.data);
            // Special case: insulation step with IS1 configured uses alternative next link
            if (step === "opbouw.isolatie" && product.properties.IS1) {
                setNextLink(nextLinkSec);
            }
        });
    }

    /**
     * Update properties with optional server persistence
     * Main property update function with server synchronization
     *
     * @param {Object} props - Updated properties object
     * @param {boolean} doRequest - Whether to save to server (default: true)
     */
    function updateProps(props, doRequest = true) {
        if (doRequest) {
            api.put(saveUrl, { properties: props }).then((response) => {
                setProductprops(response.data);
				setProperties(response.data.properties);
                updateSteps();
            });
        }
        setProperties(props);
    }

    /**
     * Update individual property value with server persistence
     * Simplified property update for single values
     *
     * @param {*} value - New property value
     * @param {string} property - Property name being updated
     * @param {boolean} doRequest - Whether to save to server (default: true)
     */
    function updateQuestion(value, property, doRequest = true) {
        let props = { ...properties };
        props[property] = value;
        setProperties(props);

        if (doRequest) {
            api.put(saveUrl, { properties: props }).then((response) => {
                setProductprops(response.data);
                updateSteps();
            });
        }
    }

    /**
     * Update the questions array for dynamic form generation
     * Used when questions need to be modified based on configuration
     *
     * @param {Array} newquestions - Updated questions array
     */
    function updateQuestions(newquestions) {
        setQuests(newquestions);
    }

    /**
     * Validate current configuration step
     * Performs comprehensive validation based on the current step and materials selected
     *
     * @returns {boolean} Whether validation passed
     */
    function checkStep() {
        let errors = [];
				let unknownMaterials = ['75515056', '75515066'];

		// Outer layer validation
		if (step === "opbouw.buitenlaag") {
			const isUnknown = unknownMaterials.includes(properties.bu_material);

			if (isUnknown) {
				if (materialNotFilled('BU_UNK')) {
					errors.push('BU_UNK');
				}
			}else {
				if (materialNotFilled("BU")) {
					errors.push("BU");
				}
				// Special material validation for specific material type
				if (
					properties.bu_material === "36256462" &&
					materialNotFilled("CO_BU")
				) {
					errors.push("CO_BU");
				}
				if (
					properties.bu_material === "36256462" &&
					materialNotFilled("KL_BU")
				) {
					errors.push("KL_BU");
				}
				// Scrap material requires direction specification
				if (
					productprops?.products?.BU?.name.includes("SCHROOT") &&
					materialNotFilled("SCHROOT_RI")
				) {
					errors.push("SCHROOT_RI");
				}
			}
        }

        // Inner layer validation
        if (step === "opbouw.binnenlaag") {
			const isUnknown = unknownMaterials.includes(properties.bi_material);

			if (isUnknown) {
				if (materialNotFilled('BI_UNK')) {
					errors.push('BI_UNK');
				}
			}else {
				if (materialNotFilled("BI")) {
					errors.push("BI");
				}
				if (
					properties.bi_material === "36256462" &&
					materialNotFilled("CO_BI")
				) {
					errors.push("CO_BI");
				}
				if (
					properties.bi_material === "36256462" &&
					materialNotFilled("KL_BI")
				) {
					errors.push("KL_BI");
				}
			}
        }

        // Core plate validation
        if (step === "opbouw.kernplaat") {
			const isUnknown = unknownMaterials.includes(properties.kp_material);

			if (isUnknown) {
				if (materialNotFilled('TL_UNK')) {
					errors.push('TL_UNK');
				}
			}else {
				// TODO: for now I just commented this because "KP" isn't the name of this layer, it's "TL"
				// This validation was never even triggered to begin with due to the validateCheck variable
				// I need validation now for the unknown layers though, so I had to add this step to validation
				// if (materialNotFilled("KP")) {
				// 	errors.push("KP");
				// }
			}
        }

        // Insulation validation
        if (step === "opbouw.isolatie") {
			const isUnknown = unknownMaterials.includes(properties.is_material);

			if (isUnknown) {
				if (materialNotFilled('IS1_UNK')) {
					errors.push('IS1_UNK');
				}
				if (
					productprops.layer === "vijflaags" &&
					materialNotFilled("IS2_UNK")
				) {
					errors.push('IS2_UNK');
				}
			}else {
				if (materialNotFilled("IS1")) {
					errors.push("IS1");
				}
				if (
					productprops.layer === "vijflaags" &&
					materialNotFilled("IS2")
				) {
					errors.push("IS2");
				}
			}
        }

        // Individual insulation layer validation
        if (step === "opbouw.isolatie1") {
			const isUnknown = unknownMaterials.includes(properties.is_material_1);

			if (isUnknown) {
				if (materialNotFilled('IS1_UNK')) {
					errors.push('IS1_UNK');
				}
			}else {
				if (materialNotFilled("IS1")) {
					errors.push("IS1");
				}
			}
		}
		if (step === "opbouw.isolatie2") {
			const isUnknown = unknownMaterials.includes(properties.is_material_2);

			if (isUnknown) {
				if (materialNotFilled('IS2_UNK')) {
					errors.push('IS2_UNK');
				}
			}else {
				if (materialNotFilled("IS2")) {
					errors.push("IS2");
				}
			}
        }

        setErrors(errors);
        return errors.length <= 0;
    }

    /**
     * Handle shopping cart navigation with validation
     * Only allows navigation if current step is valid
     *
     * @returns {boolean} Whether navigation is allowed
     */
    function clickShopping() {
        return checkStep();
    }

    /**
     * Handle step navigation with validation
     * Validates current step before allowing navigation
     *
     * @param {Event} e - Click event
     * @param {string} link - Target navigation URL
     */
    function clickStep(e, link) {
        e.preventDefault();
        let check = checkStep();
        if (check) {
            Inertia.visit(link);
        }
    }

    /**
     * Handle previous step navigation with validation
     */
    function checkPrev() {
        let check = checkStep();
        if (check) {
            Inertia.visit(prevLink);
        }
    }

    /**
     * Handle next step navigation with validation
     */
    function checkNext() {
        let check = checkStep();
        if (check) {
            Inertia.visit(nextLink);
        }
    }

    /**
     * Check if a material property is not filled/selected
     * Helper function for validation logic
     *
     * @param {string} property - Property name to check
     * @returns {boolean} Whether the property is empty/not selected
     */
    function materialNotFilled(property) {
        return (
            properties[property] === 0 ||
            properties[property] === "" ||
            !properties[property]
        );
    }

    /**
     * Update panel thickness with input validation
     * Special handler for PU panel thickness input with numeric validation
     *
     * @param {string} value - Input value from thickness field
     */
    function updateThickness(value) {
        // Remove non-numeric characters
        let newVal = value.replace(/\D/g, "");

        let props = { ...properties };
        props.tickness = newVal;
        setProperties(props);

        // Prepare data for server update
        let updateProps = Object.assign({}, props);
        if (newVal === "") {
            updateProps.tickness = 0;
        }

        // Save to server and update product data
        api.put(saveUrl, { properties: updateProps }).then((response) => {
            setProductprops(response.data);
            updateSteps();
        });
    }

    /**
     * Prevent decimal input in thickness field
     * Blocks comma and period characters for integer-only input
     *
     * @param {Event} e - Keypress event
     */
    function checkKey(e) {
        e = e || window.event;
        if (e.charCode === 44 || e.charCode === 46) {
            e.preventDefault();
        }
    }

    return (
        <Layout>
            <Sidepanel
                steps={stepList}
                validates={step === "opbouw.buitenlaag"}
                product={product}
                clickStep={clickStep}
            />
            <Content
                clickShopping={clickShopping}
                steps={stepList}
                shoppingurl={shoppingurl}
                product={product}
                session={session}
                validates={step === "opbouw.buitenlaag"}
            >
                <ContentOptions>
                    <Heading heading={heading} subheading={subheading} />
                    {quests.map((question, key) => (
                        <Question
                            errors={errors}
                            key={key}
                            question={question}
                            properties={properties}
                            updateQuestionProperties={updateQuestionProperties}
                            updateProperties={updateProps}
                            updateQuestion={updateQuestion}
                        />
                    ))}
                    {step === "opbouw.isolatie" &&
                        (product.layer === "tweelaags" ||
                            product.layer === "drielaags") &&
                        productprops.products.IS1 &&
                        productprops.products.IS1.alias.includes("PU") && (
                            <>
                                <Label label={"Dikte paneel (in mm)"} />
                                <input
                                    type="number"
                                    value={properties.tickness || ""}
                                    onChange={(e) =>
                                        updateThickness(e.target.value)
                                    }
                                    onKeyPress={checkKey}
                                    className={`form-control ${errors && errors.algemeen && errors.algemeen.includes("DIKTE") ? "is-invalid" : ""}`}
                                />
                                {/*<TextInput properties={properties} field={field} property={field.property}*/}
                                {/*updateProperties={updateProperties}/>*/}
                            </>
                        )}
                    <NavigationButtons
                        prevlink={
                            step === "opbouw.buitenlaag"
                                ? false
                                : validateCheck
                                  ? checkPrev
                                  : prevLink
                        }
                        nextlink={validateCheck ? checkNext : nextLinkObj}
                        nextApi={validateCheck}
                        prevApi={validateCheck}
                    />
                </ContentOptions>
                <Drawing
                    session_product={productprops}
                    properties={properties}
                    products={productprops.products}
                    sponningside={properties.sponning_rondom}
                    insideview={insideview}
                    colors={colors}
                />
            </Content>
        </Layout>
    );
}
